/**
 * Custom deferred media component that preloads videos when coming into view,
 * autoplays them and avoid stopping all videos when loading. Based on 
 * the original DeferredMedia component.
 */
if (!customElements.get('custom-deferred-media')) {
  class CustomDeferredMedia extends HTMLElement {
    constructor() {
      super();
      const poster = this.querySelector('[id^="Deferred-Poster-"]');
      if (!poster) return;
      poster.addEventListener('click', this.loadContent.bind(this));
    }

    /**
     * Customization: load content when coming into view
     */
    connectedCallback() {
      const options = { root: null, rootMargin: '0px', threshold: 0.1};

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) this.loadContent();
        });
      }, options);

      this.observer.observe(this);
    }

    /**
     * Customization: disconnect observer
     */
    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    loadContent(focus = true) {
      // Customization: Do not pause other media when loading
      // window.pauseAllMedia();

      if (!this.getAttribute('loaded')) {
        const content = document.createElement('div');
        content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

        this.setAttribute('loaded', true);
        const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
        if (focus) deferredElement.focus();

        // Customization: Autoplay videos after loading (Safari will not respect autoplay attribute)
        const video = this.querySelector('video');
        if (video) video.play();
      } else {
        // Play video if paues (as media gallery stops all videos on thumbnail click)
        const video = this.querySelector('video');
        if (video && video.paused) video.play();
      }
    }
  }

  customElements.define('custom-deferred-media', CustomDeferredMedia);
}

/**
 * Custom collapsible text component that shows/hides content with a toggle button.
 * By default only active on mobile devices. When the data-always-on attribute is
 * present, the component initializes on all screen sizes.
 */
if (!customElements.get('custom-collapsible-text')) {
  class CustomCollapsibleText extends HTMLElement {
    constructor() {
      super();
      // Support both generic (.collapsible-content) and legacy collection-description class names
      this.content = this.querySelector('.collapsible-content, .collection-description__content');
      this.toggle = this.querySelector('.collapsible-toggle, .collection-description__toggle');
      this.initialized = false;
      this.onToggleClickBound = this.onToggleClick.bind(this);
      this.onBreakpointChangeBound = this.onBreakpointChange.bind(this);
      
      if (!this.content || !this.toggle) return;

      // data-always-on: initialize on all screen sizes (e.g. product description)
      if (this.hasAttribute('data-always-on')) {
        this.init();
        return;
      }
      
      // Default: only initialize on mobile
      this.mobileMediaQuery = window.matchMedia('(max-width: 576px)');
      if (this.mobileMediaQuery.addEventListener) {
        this.mobileMediaQuery.addEventListener('change', this.onBreakpointChangeBound);
      } else {
        this.mobileMediaQuery.addListener(this.onBreakpointChangeBound);
      }
      
      if (this.mobileMediaQuery.matches) {
        this.init();
      }
    }

    init() {
      if (this.initialized) return;

      if (this.hasAttribute('data-always-on')) {
        const collapsedHeight = Number(this.dataset.collapsedHeight) || 350;
        const shouldCollapse = this.content.scrollHeight > collapsedHeight + 1;

        this.toggle.hidden = !shouldCollapse;
        this.classList.toggle('is-collapsed', shouldCollapse);

        if (!shouldCollapse) return;
      } else {
        this.classList.add('is-collapsed');
      }

      this.toggle.addEventListener('click', this.onToggleClickBound);
      this.initialized = true;
    }

    onBreakpointChange(event) {
      if (event.matches) {
        this.init();
      } else {
        this.destroy();
      }
    }

    onToggleClick() {
      // Capture expanded state before toggling
      const isExpanded = !this.classList.contains('is-collapsed');
      this.classList.toggle('is-collapsed');
      // On collapse, scroll back to the top of the block.
      // Only for data-always-on elements (product description), not collection description.
      if (isExpanded && this.hasAttribute('data-always-on')) {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    destroy() {
      if (!this.initialized) return;
      this.toggle.removeEventListener('click', this.onToggleClickBound);
      this.classList.remove('is-collapsed');
      this.toggle.hidden = false;
      this.initialized = false;
    }
  }

  customElements.define('custom-collapsible-text', CustomCollapsibleText);
}


/**
 * Custom product finder line item component that adds the product finder line item attribute on product page 
 * for tracking of product finder purchases.
 */
if (!customElements.get('line-item-source')) {
  class LineItemSource extends HTMLElement {
    connectedCallback() {
      const supportedSources = new Set(['product_finder']);
      const params = new URLSearchParams(window.location.search);
      const sourceValue = params.get('source');
      if (!sourceValue || !supportedSources.has(sourceValue)) return;

      const input = this.querySelector('.line-item-source__input') || this.querySelector('input');
      if (!input) return;

      input.value = sourceValue;
      input.disabled = false;
    }
  }

  customElements.define('line-item-source', LineItemSource);
}

/**
 * Product page cross-sell component.
 * Intercepts the main add-to-cart submit so the original product can be added
 * before showing the popup, while the cart drawer waits for the shopper's choice.
 */
if (!customElements.get('product-cross-sell')) {
  class ProductCrossSell extends HTMLElement {
    constructor() {
      super();
      this.pendingFormData = null;
      this.originalCartState = null;
      this.originalVariantId = null;
      this.selectedVariantId = null;
      this.isSubmitting = false;
      this.onSubmitBound = this.onSubmit.bind(this);
      this.onModalClickBound = this.onModalClick.bind(this);
      this.onModalClosedBound = this.onModalClosed.bind(this);
    }

    connectedCallback() {
      this.productForm = this.querySelector('product-form');
      this.form = this.productForm && this.productForm.querySelector('form');
      this.modal = document.getElementById(this.dataset.modalId);
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      if (!this.form || !this.modal || !this.cart) return;

      // Capture phase keeps this custom flow ahead of the theme product-form submit handler.
      this.form.addEventListener('submit', this.onSubmitBound, true);
      this.modal.addEventListener('click', this.onModalClickBound);
      document.body.addEventListener('modalClosed', this.onModalClosedBound);
    }

    disconnectedCallback() {
      if (this.form) this.form.removeEventListener('submit', this.onSubmitBound, true);
      if (this.modal) this.modal.removeEventListener('click', this.onModalClickBound);
      document.body.removeEventListener('modalClosed', this.onModalClosedBound);
    }

    onSubmit(event) {
      if (this.isSubmitting || !this.modal || !this.cart) return;
      if (event.submitter && event.submitter.name !== 'add') return;

      event.preventDefault();
      event.stopImmediatePropagation();

      this.submitButton = this.productForm.querySelector('[name="add"]');
      if (this.submitButton && this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.pendingFormData = new FormData(this.form);
      this.originalCartState = null;
      this.originalVariantId = this.pendingFormData.get('id');
      this.selectedVariantId = null;
      this.setLoading(true);

      if (typeof this.cart.setActiveElement === 'function') {
        this.cart.setActiveElement(document.activeElement);
      }

      this.addOriginalProductAndOpenModal();
    }

    onModalClick(event) {
      if (!this.pendingFormData) return;

      if (event.target === this.modal) {
        this.selectedVariantId = null;
        this.closeModal();
        return;
      }

      const addButton = event.target.closest('[data-cross-sell-add]');
      if (addButton && this.modal.contains(addButton)) {
        this.selectedVariantId = addButton.dataset.variantId;
        this.closeModal();
        return;
      }

      const closeButton = event.target.closest('[data-cross-sell-close]');
      if (closeButton && this.modal.contains(closeButton)) {
        this.selectedVariantId = null;
        this.closeModal();
      }
    }

    onModalClosed() {
      if (!this.pendingFormData || this.isSubmitting) return;

      if (this.selectedVariantId) {
        this.addSelectedProductAndOpenCart();
        return;
      }

      this.openCartDrawer(this.originalCartState);
      this.resetState();
    }

    closeModal() {
      if (typeof this.modal.hide === 'function') {
        this.modal.hide();
      }
    }

    buildLineItem(formData) {
      const item = {
        id: Number(formData.get('id')),
        quantity: Number(formData.get('quantity')) || 1
      };
      const properties = {};

      formData.forEach((value, key) => {
        if (!value) return;

        if (key.indexOf('properties[') === 0) {
          const propertyName = key.replace('properties[', '').replace(']', '');
          properties[propertyName] = value;
        }

        if (key === 'selling_plan') {
          item.selling_plan = value;
        }
      });

      if (Object.keys(properties).length > 0) item.properties = properties;

      return item;
    }

    buildCartBody(items) {
      return {
        items: items,
        sections: this.cart.getSectionsToRender().map((section) => section.id),
        sections_url: getCartSectionsUrl()
      };
    }

    async addItemsToCart(items) {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify(this.buildCartBody(items));

      const response = await fetch(`${routes.cart_add_url}`, config);
      return response.json();
    }

    async addOriginalProductAndOpenModal() {
      this.isSubmitting = true;
      this.error = false;

      const mainItem = this.buildLineItem(this.pendingFormData);
      this.originalVariantId = mainItem.id;

      try {
        const parsedState = await this.addItemsToCart([mainItem]);

        if (parsedState.status) {
          this.handleCartError(parsedState, mainItem.id);
          this.resetState();
          return;
        }

        this.originalCartState = parsedState;
        this.isSubmitting = false;

        if (typeof this.modal.show === 'function') {
          this.modal.show(this.submitButton);
        } else {
          this.openCartDrawer(this.originalCartState);
          this.resetState();
        }
      } catch (error) {
        console.error('Cross-sell cart add failed.', error);
        this.resetState();
      }
    }

    async addSelectedProductAndOpenCart() {
      this.isSubmitting = true;
      this.error = false;

      try {
        const parsedState = await this.addItemsToCart([{
          id: Number(this.selectedVariantId),
          quantity: 1
        }]);

        if (parsedState.status) {
          this.handleCartError(parsedState, this.selectedVariantId);
          this.openCartDrawer(this.originalCartState);
          return;
        }

        this.openCartDrawer(parsedState);
      } catch (error) {
        console.error('Cross-sell cart add failed.', error);
        this.openCartDrawer(this.originalCartState);
      } finally {
        this.resetState();
      }
    }

    openCartDrawer(parsedState) {
      if (!parsedState) return;

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'product-form',
        productVariantId: String(this.originalVariantId)
      });
      this.cart.renderContents(parsedState);
    }

    resetState() {
      this.pendingFormData = null;
      this.originalCartState = null;
      this.originalVariantId = null;
      this.selectedVariantId = null;
      this.isSubmitting = false;
      this.setLoading(false);
    }

    handleCartError(response, productVariantId) {
      this.error = true;
      publish(PUB_SUB_EVENTS.cartError, {
        source: 'product-form',
        productVariantId: String(productVariantId),
        errors: response.description,
        message: response.message
      });

      if (typeof this.productForm.handleErrorMessage === 'function') {
        this.productForm.handleErrorMessage(response.description);
      }
    }

    setLoading(isLoading) {
      const submitButton = this.submitButton || this.productForm.querySelector('[name="add"]');
      const spinner = this.productForm.querySelector('.loading-overlay__spinner');

      if (submitButton) {
        submitButton.classList.toggle('loading', isLoading);

        if (isLoading) {
          submitButton.setAttribute('aria-disabled', true);
        } else if (!this.error) {
          submitButton.removeAttribute('aria-disabled');
        }
      }

      if (spinner) spinner.classList.toggle('hidden', !isLoading);
      if (!isLoading && this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
    }
  }

  customElements.define('product-cross-sell', ProductCrossSell);
}

/**
 * Promo banner countdown timer
 * Displays a live countdown timer for promotional banners
 */
document.addEventListener('DOMContentLoaded', function() {
  const countdownTimers = document.querySelectorAll('.promo-banner__countdown-timer[data-countdown-end]');
  
  countdownTimers.forEach(function(timerEl) {
    const endDateStr = timerEl.getAttribute('data-countdown-end');
    const endedText = timerEl.getAttribute('data-countdown-ended') || 'Akce skončila';
    const daySingular = timerEl.getAttribute('data-day-singular') || 'den';
    const dayPluralFew = timerEl.getAttribute('data-day-plural-few') || 'dny';
    const dayPluralMany = timerEl.getAttribute('data-day-plural-many') || 'dní';
    
    const endDate = new Date(endDateStr).getTime();
    
    if (isNaN(endDate)) {
      timerEl.textContent = 'Neplatné datum';
      return;
    }

    function updateCountdown() {
      const now = new Date().getTime();
      const distance = endDate - now;

      if (distance < 0) {
        timerEl.textContent = endedText;
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      let timeStr = '';
      if (days > 0) {
        const dayLabel = days === 1 ? daySingular : (days >= 2 && days <= 4 ? dayPluralFew : dayPluralMany);
        timeStr = days + ' ' + dayLabel + ', ';
      }
      timeStr += String(hours).padStart(2, '0') + ':' +
                 String(minutes).padStart(2, '0') + ':' +
                 String(seconds).padStart(2, '0');

      timerEl.textContent = timeStr;
    }

    updateCountdown();
    setInterval(updateCountdown, 1000);
  });
});

/**
 * Product page: Other motives button state handling
 * Toggles arrow direction while modal is open.
 */
document.addEventListener('DOMContentLoaded', function() {
  const otherMotiveButtons = document.querySelectorAll('.other-motives__button[data-other-motives-modal-id]');
  const otherMotiveModals = document.querySelectorAll('.other-motives-modal');

  otherMotiveButtons.forEach(function(button) {
    button.addEventListener('click', function() {
      button.classList.add('is-open');
    });
  });

  otherMotiveModals.forEach(function(modal) {
    modal.addEventListener('click', function(event) {
      if (event.target === modal && typeof modal.hide === 'function') {
        modal.hide();
      }
    });
  });

  document.body.addEventListener('modalClosed', function() {
    otherMotiveButtons.forEach(function(button) {
      button.classList.remove('is-open');
    });
  });
});

/**
 * Wishlist page: bridge Hulk add-to-cart to the theme cart drawer refresh.
 * Hulk updates the cart in the background, but doesn't trigger the theme's
 * native cart drawer rendering flow on the app proxy page.
 */
document.addEventListener('DOMContentLoaded', function() {
  if (!window.location.pathname.startsWith('/apps/advanced-wishlist')) return;

  const wishlistRoot = document.querySelector('.hulk__wishlist-wrap');
  if (!wishlistRoot) return;

  let cartRefreshInFlight = false;

  function isWishlistAddToCartTrigger(element) {
    const trigger = element.closest('[data-addmywishlistprotocart]');
    return !!(trigger && wishlistRoot.contains(trigger));
  }

  function wait(milliseconds) {
    return new Promise((resolve) => setTimeout(resolve, milliseconds));
  }

  function getCartSignature(cartState) {
    return JSON.stringify({
      item_count: cartState.item_count,
      total_price: cartState.total_price,
      items: cartState.items.map((item) => ({
        key: item.key,
        quantity: item.quantity,
        final_line_price: item.final_line_price
      }))
    });
  }

  async function fetchCartState() {
    const response = await fetch(`${window.routes.cart_url}.js`, {
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) throw new Error('Failed to fetch cart state');

    return response.json();
  }

  async function refreshCartDrawer() {
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    if (!cart || typeof cart.renderContents !== 'function') return;

    const sectionsUrl = `${getCartSectionsUrl()}?sections=cart-drawer,cart-icon-bubble,cart-icon-bubble--mobile,cart-free-delivery`;
    const response = await fetch(sectionsUrl, {
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json'
      }
    });

    if (!response.ok) throw new Error('Failed to fetch cart sections');

    const sections = await response.json();
    cart.classList.remove('is-empty');
    cart.renderContents({
      id: null,
      sections: sections
    });
  }

  /**
   * Monitor cart changes - including a followup change for gifts added to cart with the main product.
   */
  async function monitorCartUpdatesAndRefresh(previousCartState) {
    const pollInterval = 500;
    const firstChangeTimeout = 45000;
    const followUpWindow = 8000;
    const startedAt = Date.now();
    const previousSignature = getCartSignature(previousCartState);
    let refreshedSignature = previousSignature;
    let firstChangeAt = null;

    while (Date.now() - startedAt < firstChangeTimeout) {
      await wait(pollInterval);

      const cartState = await fetchCartState();
      const currentSignature = getCartSignature(cartState);

      if (currentSignature === previousSignature) continue;

      firstChangeAt = Date.now();
      refreshedSignature = currentSignature;
      await refreshCartDrawer();
      break;
    }

    if (!firstChangeAt) return;

    while (Date.now() - firstChangeAt < followUpWindow) {
      await wait(pollInterval);

      const cartState = await fetchCartState();
      const currentSignature = getCartSignature(cartState);

      if (currentSignature === refreshedSignature) continue;

      refreshedSignature = currentSignature;
      await refreshCartDrawer();
    }
  }

  wishlistRoot.addEventListener('click', async function(event) {
    if (cartRefreshInFlight || !isWishlistAddToCartTrigger(event.target)) return;

    cartRefreshInFlight = true;

    try {
      const previousCartState = await fetchCartState();
      await monitorCartUpdatesAndRefresh(previousCartState);
    } catch (error) {
      console.error('Wishlist cart drawer refresh failed.', error);
    } finally {
      cartRefreshInFlight = false;
    }
  }, true);
});